import React, { useCallback, useEffect, useState } from "react";
import { Globe, Sparkles, Newspaper, Mic, Share2, BarChart3, SlidersHorizontal, Languages } from "lucide-react";
import { api } from "@/lib/api";
import { LangHubTab } from "@/components/language/LangHubTab";
import { LangContentTab } from "@/components/language/LangContentTab";
import { LangNewsTab } from "@/components/language/LangNewsTab";
import { LangAnchorsTab } from "@/components/language/LangAnchorsTab";
import { LangSocialTab } from "@/components/language/LangSocialTab";
import { LangAnalyticsTab } from "@/components/language/LangAnalyticsTab";
import { LangSettingsTab } from "@/components/language/LangSettingsTab";

const TABS = [
  { id: "hub", label: "Language Hub", icon: Globe },
  { id: "content", label: "AI Content", icon: Sparkles },
  { id: "news", label: "Multilingual News", icon: Newspaper },
  { id: "anchors", label: "AI Voice Anchors", icon: Mic },
  { id: "social", label: "Social Localization", icon: Share2 },
  { id: "analytics", label: "Language Analytics", icon: BarChart3 },
  { id: "settings", label: "Settings", icon: SlidersHorizontal },
];

export default function LanguageCenter() {
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("hub");

  const loadOverview = useCallback(async (sid) => {
    try {
      const r = await api.get("/language-center/overview", { params: { station_id: sid || "" } });
      setOverview(r.data);
    } catch { /* no station yet */ }
  }, []);

  useEffect(() => {
    api.get("/stations").then((r) => {
      const list = r.data || [];
      setStations(list);
      const sid = list[0]?.station_id || "";
      setStationId(sid);
      loadOverview(sid);
    }).catch(() => {});
  }, [loadOverview]);

  const changeStation = (sid) => { setStationId(sid); loadOverview(sid); };
  const refresh = useCallback(() => loadOverview(stationId), [loadOverview, stationId]);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="language-center-page">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="rounded-xl bg-sky-500/15 p-2.5"><Languages className="h-6 w-6 text-sky-300" /></div>
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-display text-2xl font-bold text-ink-primary">Global Language Center</h1>
          <p className="text-xs text-ink-muted">
            Multilingual AI · {overview?.languages?.all?.length || 14} Languages · Create Once, Broadcast Everywhere
          </p>
        </div>
        {stations.length > 1 && (
          <select
            value={stationId} onChange={(e) => changeStation(e.target.value)} data-testid="lc-station-select"
            className="rounded-lg border border-line bg-bg-surface px-3 py-2 text-sm text-ink-primary focus:outline-none focus:border-brand-amber/50"
          >
            {stations.map((s) => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
          </select>
        )}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-200" data-testid="lc-language-count-badge">
          <Languages className="h-3.5 w-3.5" /> {overview?.languages?.all?.length || 14} Languages
        </span>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-line mb-8" data-testid="lc-tabs">
        {TABS.map((t) => (
          <button
            key={t.id} onClick={() => setTab(t.id)} data-testid={`lc-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id
                ? "border-brand-amber text-ink-primary"
                : "border-transparent text-ink-muted hover:text-ink-secondary"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "hub" && <LangHubTab overview={overview} stationId={stationId} refresh={refresh} />}
      {tab === "content" && <LangContentTab overview={overview} stationId={stationId} />}
      {tab === "news" && <LangNewsTab overview={overview} stationId={stationId} />}
      {tab === "anchors" && <LangAnchorsTab stationId={stationId} />}
      {tab === "social" && <LangSocialTab overview={overview} stationId={stationId} />}
      {tab === "analytics" && <LangAnalyticsTab />}
      {tab === "settings" && <LangSettingsTab overview={overview} stationId={stationId} refresh={refresh} />}
    </div>
  );
}
